import { CommonModule, DatePipe, DOCUMENT } from '@angular/common';
import { Component, effect, HostListener, inject, input, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowLeft,
  lucideArrowRight,
  lucideExternalLink,
  lucideHeadphones,
  lucideInfo,
  lucidePause,
  lucidePlay,
  lucideShare2,
} from '@ng-icons/lucide';
import { AvatarComponent, AvatarType } from '@null-decision/avatars';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ErrorMessageComponent } from '../../components/error-message/error-message.component';
import { LoadingSpinnerComponent } from '../../components/loading-spinner/loading-spinner.component';
import { NewsArticle, NewsCardThemeColor } from '../../models/news-article.model';
import { ExtractDomainPipe } from '../../pipes/extract-domain.pipe';
import { NewsService } from '../../services/news.service';
import { SEOService } from '../../services/seo.service';
import { SpeechSynthesisService, VoiceState } from '../../services/speech-synthesis.service';
import { StructuredDataService } from '../../services/structured-data.service';

// Define theme color mappings (used in the component template)
const THEME_COLOR_CLASSES: Record<NewsCardThemeColor, { light: string; dark: string }> = {
  rose: { light: 'bg-rose-100', dark: 'bg-[#893f45]/80' },
  pink: { light: 'bg-pink-100', dark: 'bg-pink-950/80' },
  fuchsia: { light: 'bg-fuchsia-100', dark: 'bg-[#5b3256]/80' },
  purple: { light: 'bg-purple-100', dark: 'bg-[#301934]/80' },
  violet: { light: 'bg-violet-100', dark: 'bg-[#321746]/80' },
  indigo: { light: 'bg-indigo-100', dark: 'bg-[#1e2952]/80' },
  blue: { light: 'bg-blue-100', dark: 'bg-blue-950/80' },
  sky: { light: 'bg-sky-100', dark: 'bg-sky-950/80' },
  emerald: { light: 'bg-emerald-100', dark: 'bg-emerald-950/80' },
  green: { light: 'bg-green-100', dark: 'bg-[#3b3c36]/80' },
  lime: { light: 'bg-lime-100', dark: 'bg-[#4a412a]/80' },
  amber: { light: 'bg-amber-100', dark: 'bg-[#44362f]/80' },
  orange: { light: 'bg-orange-100', dark: 'bg-[#722f37]/80' },
};

@Component({
  selector: 'app-article-detail',
  imports: [
    CommonModule,
    RouterLink,
    DatePipe,
    LoadingSpinnerComponent,
    ErrorMessageComponent,
    AvatarComponent,
    ExtractDomainPipe,
    HlmButtonDirective,
    HlmIconDirective,
    NgIcon,
  ],
  templateUrl: './article-detail.component.html',
  providers: [
    provideIcons({
      lucideShare2,
      lucideExternalLink,
      lucideArrowLeft,
      lucideArrowRight,
      lucidePlay,
      lucidePause,
      lucideHeadphones,
      lucideInfo,
    }),
  ],
})
export class ArticleDetailComponent implements OnInit, OnDestroy {
  private newsService = inject(NewsService);
  private seoService = inject(SEOService);
  private structuredDataService = inject(StructuredDataService);
  private document = inject(DOCUMENT);
  private router = inject(Router);
  private speechService = inject(SpeechSynthesisService);

  slug = input('');

  article: NewsArticle | null = null;
  loading = true;
  error: string | null = null;

  // Adjacent articles navigation
  // Rename variables to match their UI representation
  nextArticle: NewsArticle | null = null; // Previously "previousArticle" - shown as "Next" on left side
  prevArticle: NewsArticle | null = null; // Previously "nextArticle" - shown as "Previous" on right side
  loadingAdjacent = false;

  // Properties to hold the selected Tailwind classes
  selectedBgClassLight = '';
  selectedBgClassDark = '';

  // Avatar configuration
  readonly avatarType: AvatarType = 'marble';
  readonly avatarColors = ['#0a0310', '#49007e', '#ff005b', '#ff7d10', '#ffb238'];

  // Speech synthesis state
  voiceState: VoiceState = 'idle';
  private speechStateSubscription: Subscription | null = null;

  constructor() {
    // Watch for changes to the slug input and reload article data
    effect(() => {
      const currentSlug = this.slug();
      if (currentSlug) {
        this.loadArticle(currentSlug);
      }
    });
  }

  ngOnInit() {
    // Subscribe to speech service state changes
    this.speechStateSubscription = this.speechService.voiceState$.subscribe(state => {
      this.voiceState = state;
    });

    // Initial load will be handled by the effect in constructor
    if (this.slug()) {
      this.loadArticle(this.slug());
    }
  }

  private loadArticle(slug: string): void {
    if (!slug) {
      this.error = 'Article slug not found';
      this.loading = false;
      return;
    }

    // Reset state
    this.loading = true;
    this.error = null;
    this.article = null;

    // Important: Clear prev/next articles to avoid showing stale data
    this.nextArticle = null;
    this.prevArticle = null;
    this.loadingAdjacent = true;

    this.newsService.getArticleBySlug(slug).subscribe({
      next: article => {
        // Wait a tick to avoid cached data showing
        setTimeout(() => {
          this.article = article;
          const colorPair = THEME_COLOR_CLASSES[this.article.image.theme];
          this.selectedBgClassLight = colorPair.light;
          this.selectedBgClassDark = colorPair.dark;
          this.loading = false;

          this.setArticleSeoMetadata(article);

          // Structured Data: Add base nodes first
          this.structuredDataService.addBaseNodes();
          // Structured Data: Add breadcrumbs
          this.addBreadcrumbsStructuredData(article); // Renamed for clarity

          // Fetch adjacent articles, which will then trigger NewsArticle and schema generation
          this.fetchAdjacentArticles(article);
        }, 0);
      },
      error: () => {
        this.error = 'Failed to load article';
        this.loading = false;
        this.loadingAdjacent = false;
      },
    });
  }

  /**
   * Fetch chronologically adjacent articles for navigation
   * In our UI:
   * - Left side shows "Next Article" which is chronologically NEWER (more recent)
   * - Right side shows "Previous Article" which is chronologically OLDER (less recent)
   */
  private fetchAdjacentArticles(article: NewsArticle): void {
    if (!article) return;

    console.log('Fetching adjacent articles for:', article.title, 'ID:', article.id, 'Type:', article.type);
    console.log('Article timestamp:', article.createdAt || article.updatedAt);

    // Ensure we're starting with clean state
    this.nextArticle = null;
    this.prevArticle = null;
    this.loadingAdjacent = true;

    // Use the simple approach first, fallback to timestamp-based if needed
    this.newsService.getAdjacentArticlesSimple(article, article.type).subscribe({
      next: adjacentArticles => {
        console.log('Simple adjacent articles response:', adjacentArticles);

        if (this.article?.id === article.id) {
          this.nextArticle = adjacentArticles.previous; // API 'previous' is chronologically newer (UI next)
          this.prevArticle = adjacentArticles.next; // API 'next' is chronologically older (UI previous)
          this.loadingAdjacent = false;

          // Structured Data: Add NewsArticle and generate schema
          if (this.article) {
            // Pass component's prevArticle (older) as service's previousArticle
            // Pass component's nextArticle (newer) as service's nextArticle
            this.structuredDataService.addNewsArticle(this.article, this.prevArticle, this.nextArticle);
            this.structuredDataService.generateAndInjectSchema();
          }

          if (!adjacentArticles.previous && !adjacentArticles.next) {
            console.log('No adjacent articles found using simple approach - trying timestamp-based approach');
            // Fallback to timestamp-based approach
            this.fetchAdjacentArticlesTimestamp(article);
          } else {
            console.log('Successfully found adjacent articles:', {
              previous: adjacentArticles.previous?.title || 'none',
              next: adjacentArticles.next?.title || 'none',
            });
          }
        }
      },
      error: error => {
        console.error('Error fetching adjacent articles (simple):', error);
        // Fallback to timestamp-based approach only if we're still on the same article
        if (this.article?.id === article.id) {
          this.fetchAdjacentArticlesTimestamp(article);
        }
      },
    });
  }

  /**
   * Fallback method using timestamp-based adjacent articles
   * Uses the same chronological concept as the simple approach:
   * - 'previous' from API = chronologically NEWER = nextArticle (left side in UI)
   * - 'next' from API = chronologically OLDER = prevArticle (right side in UI)
   */
  private fetchAdjacentArticlesTimestamp(article: NewsArticle): void {
    console.log('Trying timestamp-based adjacent articles approach');

    this.newsService.getAdjacentArticles(article, article.type).subscribe({
      next: adjacentArticles => {
        console.log('Timestamp-based adjacent articles response:', adjacentArticles);

        if (this.article?.id === article.id) {
          this.nextArticle = adjacentArticles.previous; // API 'previous' is chronologically newer (UI next)
          this.prevArticle = adjacentArticles.next; // API 'next' is chronologically older (UI previous)
          this.loadingAdjacent = false;

          // Structured Data: Add NewsArticle and generate schema
          if (this.article) {
            // Pass component's prevArticle (older) as service's previousArticle
            // Pass component's nextArticle (newer) as service's nextArticle
            this.structuredDataService.addNewsArticle(this.article, this.prevArticle, this.nextArticle);
            this.structuredDataService.generateAndInjectSchema();
          }

          if (!adjacentArticles.previous && !adjacentArticles.next) {
            console.log('No adjacent articles found with either approach - this might be the only article');
          }
        }
      },
      error: error => {
        console.error('Error fetching adjacent articles (timestamp):', error);
        if (this.article?.id === article.id) {
          this.loadingAdjacent = false;
        }
      },
    });
  }

  getLatestTimestamp(): Date {
    if (!this.article?.sources?.length) return new Date();
    return new Date(Math.max(...this.article.sources.map(source => new Date(source.timestamp).getTime())));
  }

  /**
   * Get the article's creation timestamp for ordering
   */
  getArticleTimestamp(): Date {
    if (!this.article) return new Date();
    return new Date(this.article.createdAt || this.article.updatedAt || '');
  }

  async shareArticle() {
    if (!this.article) return;

    if (navigator.share) {
      try {
        await navigator.share({
          title: this.article.title,
          text: this.article.summary,
          url: window.location.href,
        });
      } catch {
        // Ignore share errors (e.g. user cancellation)
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch {
        // Ignore clipboard errors
      }
    }
  }

  /**
   * Set SEO metadata for a specific article
   */
  private setArticleSeoMetadata(article: NewsArticle): void {
    if (!article) return;

    // Get primary source and publication date
    let sourceUrl = '';
    let publishDate = '';

    if (article.sources && article.sources.length > 0) {
      sourceUrl = article.sources[0].url;
      publishDate = article.sources[0].timestamp;
    }

    // Extract domain as "publisher" for schema
    let publisher = '';
    if (sourceUrl) {
      try {
        const url = new URL(sourceUrl);
        publisher = url.hostname.replace('www.', '');
      } catch {
        // Ignore URL parsing errors
      }
    }

    // Create a description that's SEO friendly
    const seoDescription = article.summary.length > 160 ? `${article.summary.substring(0, 157)}...` : article.summary;

    // Use article.keywords if available, otherwise fallback to dynamic keyword generation
    let finalKeywords = '';
    if (article.keywords && article.keywords.length > 0) {
      finalKeywords = article.keywords.join(', ');
    } else {
      const titleKeywords = article.title
        .toLowerCase()
        .split(' ')
        .filter(kw => kw.length > 3);
      const summaryKeywords = article.summary
        .toLowerCase()
        .split(' ')
        .filter(kw => kw.length > 3);
      const baseKeywords = ['AI news', 'artificial intelligence', 'tech news', 'news summaries', 'machine learning'];
      // Remove duplicates and limit the number of keywords
      finalKeywords = [...new Set([...baseKeywords, ...titleKeywords, ...summaryKeywords])].slice(0, 10).join(', ');
    }

    // Create image URL if available
    const imageUrl = '/assets/og/black-og-image-1x.png';

    // Create a definitive canonical URL for this article
    // This is CRITICAL for SEO to prevent duplicate content issues
    const canonicalUrl = `${environment.canonicalOrigin}/news/${article.slug}`;

    this.seoService.updateTags({
      title: `${article.title} | Summerry`,
      description: seoDescription,
      keywords: finalKeywords,
      type: 'article',
      image: imageUrl,
      url: canonicalUrl, // Explicitly set the canonical URL
      publishedTime: publishDate,
      author: publisher,
    });

    // Add structured data for this article
    // this.structuredDataService.addArticleStructuredData(article);
  }

  onNavigateToNews() {
    this.router.navigate(['/news']);
  }

  private addBreadcrumbsStructuredData(article: NewsArticle): void {
    // Renamed for clarity
    if (!article) return;

    const breadcrumbItems = [
      { name: 'Home', item: `${environment.canonicalOrigin}/` },
      { name: 'News', item: `${environment.canonicalOrigin}/news` },
      { name: article.title }, // Current page, no item URL
    ];
    this.structuredDataService.addBreadcrumb(breadcrumbItems);
  }

  toggleSpeech() {
    if (!this.article) return;

    const text = `${this.article.title}. ${this.article.summary}`;
    this.speechService.toggle(text);
  }

  // HostListener to pause/resume speech on window blur/focus
  @HostListener('window:focus')
  onFocus() {
    if (this.voiceState === 'playing') {
      this.speechService.resume();
    }
  }

  @HostListener('window:blur')
  onBlur() {
    if (this.voiceState === 'playing') {
      this.speechService.pause();
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.speechStateSubscription?.unsubscribe();

    // Stop speech when navigating away
    this.speechService.stop();
  }
}
